import React, { useState } from "react";
import { TbPlayerPlay } from "react-icons/tb";
import { useOrganizations } from "../../hooks/useOrganizations";
import { useDomainScan } from "../../hooks/useDomainScan";
import { MODULE_GROUPS, ALL_GROUP_KEYS } from "../../lib/modules";
import { Button, Field, Alert, Toggle, SettingRow } from "../../components";
import ScanResults from "./ScanResults";
import PublicIpOptions from "../../../components/PublicIpOptions";
import "./Scanner.css";

// External risk scan.
//
// Prospecting used to be a switch here that swapped the whole form. It is now its own
// nav item and its own screen (ProspectingScreen.jsx) — the two panels share the module
// picker below and nothing else, and a mode hidden behind a toggle on someone else's
// screen has no address to link to or land on. This screen is the customer-facing scan
// only: it files results against an organization.

const ExternalRiskScan = () => {
  return (
    <div className="v2-page">
      <div className="v2-pagehead">
        <div>
          <h2>External risk scan</h2>
          <p>
            Runs from our infrastructure against a public domain or public IPv4 addresses. Nothing is installed
            and nothing touches the internal network.
          </p>
        </div>
      </div>
      <StandardScan />
    </div>
  );
};

const StandardScan = () => {
  const { organizations } = useOrganizations();
  const { run, running, results, error } = useDomainScan();
  const [domain, setDomain] = useState("");
  const [publicIps, setPublicIps] = useState("");
  const [publicIpMode, setPublicIpMode] = useState("auto");
  const [orgId, setOrgId] = useState("");
  const [groups, setGroups] = useState(ALL_GROUP_KEYS);

  if (!orgId && organizations.length) setTimeout(() => setOrgId(String(organizations[0].id)), 0);

  const toggle = (k) => setGroups((g) => (g.includes(k) ? g.filter((x) => x !== k) : [...g, k]));
  const allOn = groups.length === ALL_GROUP_KEYS.length;
  const canRun = domain.trim() && orgId && groups.length > 0 && !running;

  return (
    <>
      <div className="v2-scanform">
        <div className="v2-scanform-row">
          {/* One box for both target types: a bare IPv4 runs the IP modules instead of the
              domain ones, so the label has to say so or the field reads as domains-only. */}
          <Field label="Domain or public IPs" hint="One domain, or comma-separated public IPv4 addresses, ranges, or CIDRs. Up to 256 unique IPs.">
            <input className="v2-input" value={domain} onChange={(e) => setDomain(e.target.value)}
                   placeholder="example.com or 8.8.8.8, 1.1.1.1" maxLength={8192} disabled={running} />
          </Field>
          <Field label="Organization" hint="Automatic IP discovery uses this organization's name, website and managed inventory to confirm ownership. Its registered company name helps matching when available; a company name is optional.">
            <select className="v2-input" value={orgId} onChange={(e) => setOrgId(e.target.value)} disabled={running}>
              {organizations.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          </Field>
        </div>

        <PublicIpOptions target={domain} enabled={groups.includes("infra")}
                         mode={publicIpMode} onModeChange={setPublicIpMode}
                         value={publicIps} onChange={setPublicIps} disabled={running} />

        <ModulePicker groups={groups} onToggle={toggle} allOn={allOn}
                      onToggleAll={() => setGroups(allOn ? [] : ALL_GROUP_KEYS)} disabled={running} />

        <Alert>{error}</Alert>

        <div className="v2-scanform-actions">
          <Button variant="primary" disabled={!canRun}
                  onClick={() => run({ domain: domain.trim(), groups, organizationId: orgId, publicIps, publicIpMode })}>
            <TbPlayerPlay size={15} /> {running ? "Scanning…" : "Start scan"}
          </Button>
          <span className="v2-hint" style={{ marginLeft: "auto" }}>
            {running ? "This usually takes a few minutes. Results arrive all at once." : "All four modules are on by default."}
          </span>
        </div>
      </div>

      {running && <ScanRunning domain={domain} groups={groups} />}
      {/* orgId reaches the results so managed coverage can be scoped to this client's
          synced devices. Prospecting passes none, deliberately: a prospect is not a
          client, so none of their hosts should match the managed estate. */}
      {results && !running && <ScanResults results={results} domain={domain} organizationId={orgId} />}
    </>
  );
};

export const ModulePicker = ({ groups, onToggle, allOn, onToggleAll, disabled }) => (
  <div>
    <div className="v2-modules-head">
      <span className="v2-section-title">Modules</span>
      <button type="button" className="v2-chip" style={{ marginLeft: "auto", cursor: "pointer" }}
              onClick={onToggleAll} disabled={disabled}>
        {allOn ? "Unselect all" : "Select all"}
      </button>
    </div>
    <div className="v2-modules">
      {MODULE_GROUPS.map(({ key, label, ids }) => (
        <SettingRow key={key} name={label}>
          {/* title carries the underlying ids, so the four-into-seven mapping is
              discoverable without leaving the screen */}
          <span title={`Runs: ${ids.join(", ")}`}>
            <Toggle checked={groups.includes(key)} onChange={() => onToggle(key)} disabled={disabled} ariaLabel={label} />
          </span>
        </SettingRow>
      ))}
    </div>
  </div>
);

// The scan is one blocking request with no progress payload, so this shows what was
// submitted and an indeterminate bar. A percentage here would be invented.
//
// Exported because prospecting runs the same scan through the same endpoint and had
// nothing to show for it but a disabled button reading "Scanning…" — the same wait
// presented as if it were a different, lesser thing. Shared rather than copied so the
// two cannot drift apart again.
export const ScanRunning = ({ domain, groups }) => (
  <div className="v2-running">
    <div className="v2-running-head">
      <span className="v2-dot-run" aria-hidden="true" />
      <span>Scanning</span>
      <span className="v2-mono v2-dim">{domain} · {groups.length} modules</span>
    </div>
    <div className="v2-bar"><div className="v2-bar-fill" /></div>
    <p className="v2-hint">The request returns a single result once every module has finished. Leave this tab open.</p>
  </div>
);

export default ExternalRiskScan;
